import React, { useState, useEffect } from "react";
import {
  Grid,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import API_BASE_URL from 'config/api';
import SaveCard3DSDialog from "components/Payments/SaveCard3DSDialog";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { BlueButton } from "styles";
import dayjs from "dayjs";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCardIcon from "@mui/icons-material/AddCard";
import { WhiteButton } from "styles";
import { RedButton } from "styles";
import MDBadge from "components/MDBadge";

const USER_DATA_KEY = "userMeData";

const ProfileSettings = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editClient, setEditClient] = useState(null);
  const [newClientName, setNewClientName] = useState("");
  // 3DS kart ekleme/düzenleme dialog state
  const [saveCard3DSOpen, setSaveCard3DSOpen] = useState(false);
  const [saveCardSubscriptionId, setSaveCardSubscriptionId] = useState(null);
  const [confirmRemoveCard, setConfirmRemoveCard] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Tarih formatlama
  const formatDate = (dateString) => {
    return dayjs(dateString).format("DD.MM.YYYY HH:mm");
  };

  // Kalan gün hesaplama
  const getDaysRemaining = (endDate) => {
    const end = dayjs(endDate);
    const now = dayjs();
    const days = end.diff(now, "day");
    return days > 0 ? `${days} gün` : "Süre doldu";
  };

  // Session storage'dan kullanıcı verisini al
  const getCachedUserData = () => {
    const cachedData = sessionStorage.getItem(USER_DATA_KEY);
    return cachedData ? JSON.parse(cachedData) : null;
  };

  // Kullanıcı verisini yeniden yükle
  const reloadUserData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/me`, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      });
      const data = await response.json();
      if (response.ok && data.user) {
        sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(data));
        setUserData(data);
      } else {
        throw new Error("Kullanıcı verisi alınamadı");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Önce cache'den kontrol et
    const cachedUser = getCachedUserData();
    if (cachedUser) {
      setUserData(cachedUser);
      setLoading(false);
      return;
    }

    reloadUserData();
  }, []);

  // Müşteri adını güncelleme
  const handleUpdateClient = async () => {
    if (!editClient || !newClientName) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          clientId: editClient.clientId,
          clientName: newClientName,
        }),
      });

      if (response.ok) {
        setSuccessMsg("Müşteri adı başarıyla güncellendi");
        reloadUserData();
        setEditClient(null);
      } else {
        const data = await response.json();
        throw new Error(data.message || "Güncelleme başarısız");
      }
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };


  // Kart ekleme/değiştirme (3DS) dialog kontrolü
  const openSaveCard3DS = (subscriptionId) => {
    setSaveCardSubscriptionId(subscriptionId);
    setSaveCard3DSOpen(true);
  };
  const closeSaveCard3DS = () => {
    setSaveCard3DSOpen(false);
    setSaveCardSubscriptionId(null);
  };

  // Kartı kaldır
  const handleRemoveCard = async () => {
    if (!confirmRemoveCard) return;

    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/payment/delete-card`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({
            subscriptionId: confirmRemoveCard.subscriptionId,
          }),
        }
      );

      if (response.ok) {
        setSuccessMsg("Kart başarıyla kaldırıldı");
        reloadUserData();
        setConfirmRemoveCard(null);
      } else {
        const data = await response.json();
        throw new Error(data.message || "Kart kaldırılamadı");
      }
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <MDBox my={3} display="flex" justifyContent="center">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <MDBox my={3} display="flex" justifyContent="center">
          <Typography color="error">{error}</Typography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <MDBox mt={2} mb={4}>
        <Grid container spacing={3}>
          {/* Müşteri ve Site Bilgileri */}
          {userData?.user?.clients?.map((client) => (
            <Grid item xs={12} key={client.clientId}>
              <Card sx={{ boxShadow: 3 }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                  >
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        {client.name}
                      </Typography>
                      <MDBadge
                        variant="contained"
                        badgeContent={
                          client.isActive ? "Aktif Müşteri" : "Pasif Müşteri"
                        }
                        color={client.isActive ? "success" : "error"}
                        container
                      />
                      
                    </Box>

                    <BlueButton
                      startIcon={<EditIcon />}
                      onClick={() => {
                        setEditClient(client);
                        setNewClientName(client.name);
                      }}
                    >
                      Düzenle
                    </BlueButton>
                  </Box>

                  {client.sites?.length > 0 ? (
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead
                          sx={{
                            bgcolor: "grey.100",
                            display: "table-header-group",
                          }}
                        >
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Site Bilgileri
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Abonelik
                            </TableCell>

                            <TableCell sx={{ fontWeight: "bold" }}>
                              Kart İşlemleri
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {client.sites.map((site) => (
                            <TableRow
                              key={site.siteId}
                              sx={{ verticalAlign: "top" }}
                            >
                              <TableCell>
                                <Box>
                                  <Typography variant="h5">
                                    {site.domain}
                                  </Typography>
                                  <Typography variant="body2">
                                    {`Varsayılan Dil: ${
                                      site.defaultLanguage === "en"
                                        ? "İngilizce"
                                        : site.defaultLanguage === "tr"
                                        ? "Türkçe"
                                        : site.defaultLanguage
                                    }`}
                                  </Typography>
                                  <Typography variant="body2">
                                    Abonelik Bitiş:{" "}
                                    {formatDate(site.licenseEndsAt)}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color={
                                      getDaysRemaining(
                                        site.licenseEndsAt
                                      ).includes("doldu")
                                        ? "error"
                                        : "text.primary"
                                    }
                                  >
                                    Kalan Gün:{" "}
                                    {getDaysRemaining(site.licenseEndsAt)}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {site.subscription ? (
                                  <Box>
                                    <Typography variant="body2">
                                      {site.subscription.billingCycle ===
                                      "monthly"
                                        ? "Aylık Abonelik"
                                        : "Yıllık Abonelik"}{" "}
                                      - {site.subscription.price} ₺
                                    </Typography>
                                    <Typography variant="body2">
                                      Çoklu Dil:{" "}
                                      {site.subscription.hasMultilanguage
                                        ? "Var"
                                        : "Yok"}
                                    </Typography>
                                    <Typography variant="body2">
                                      Oluşturma Tarihi:{" "}
                                      {formatDate(site.subscription.createdAt)}
                                    </Typography>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <MDBadge
                                        variant="contained"
                                        badgeContent={
                                          site.subscription?.isActive
                                            ? "Aktif"
                                            : "Pasif"
                                        }
                                        color={
                                          site.subscription?.isActive
                                            ? "success"
                                            : "error"
                                        }
                                        container
                                      />
                                      <MDBadge
                                        variant="contained"
                                        badgeContent={
                                          site.subscription?.storedCard
                                            ? "Kart Kayıtlı"
                                            : "Kart Yok"
                                        }
                                        color={
                                          site.subscription?.storedCard
                                            ? "success"
                                            : "warning"
                                        }
                                        container
                                      />
                                    </Box>
                                  </Box>
                                ) : (
                                  <Typography variant="body2">
                                    Abonelik bilgisi yok
                                  </Typography>
                                )}
                              </TableCell>

                              <TableCell>
                                {site.subscription && (
                                  <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={1}
                                  >
                                    {site.subscription.storedCard ? (
                                      <>
                                        <BlueButton
                                          startIcon={<CreditCardIcon />}
                                          onClick={() =>
                                            openSaveCard3DS(site.subscription.subscriptionId)
                                          }
                                        >
                                          Kartı Değiştir
                                        </BlueButton>
                                        <RedButton
                                          variant="outlined"
                                          color="error"
                                          startIcon={<DeleteIcon />}
                                          onClick={() =>
                                            setConfirmRemoveCard(
                                              site.subscription
                                            )
                                          }
                                        >
                                          Kartı Kaldır
                                        </RedButton>
                                      </>
                                    ) : (
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddCardIcon />}
                                        onClick={() =>
                                          openSaveCard3DS(site.subscription.subscriptionId)
                                        }
                                      >
                                        Kart Ekle
                                      </Button>
                                    )}
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography
                      variant="body1"
                      sx={{ mt: 2, textAlign: "center", py: 4 }}
                    >
                      Bu müşteriye ait site bulunamadı
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </MDBox>

      {/* Müşteri Adı Düzenleme Diyaloğu */}
      <Dialog open={!!editClient} onClose={() => setEditClient(null)}>
        <DialogTitle>Müşteri Adını Düzenle</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Müşteri Adı"
            type="text"
            fullWidth
            variant="outlined"
            value={newClientName}
            onChange={(e) => setNewClientName(e.target.value)}
            sx={{ mt: 2, minWidth: 400 }}
          />
        </DialogContent>
        <DialogActions>
          <WhiteButton onClick={() => setEditClient(null)}>İptal</WhiteButton>
          <BlueButton
            onClick={handleUpdateClient}
            disabled={!newClientName || newClientName === editClient?.name}
          >
            Kaydet
          </BlueButton>
        </DialogActions>
      </Dialog>


      <SaveCard3DSDialog
        open={saveCard3DSOpen}
        subscriptionId={saveCardSubscriptionId}
        onClose={closeSaveCard3DS}
        onSuccess={() => {
          setSuccessMsg("Kart başarıyla kaydedildi.");
          closeSaveCard3DS();
          reloadUserData();
        }}
      />

      {/* Kart Silme Onay Diyaloğu */}
      <Dialog
        open={!!confirmRemoveCard}
        onClose={() => setConfirmRemoveCard(null)}
      >
        <DialogTitle>Kartı Silme Onayı</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Kartı silmek istediğinizden emin misiniz?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Kayıtlı kart olmaması durumunda otomatik olarak üyeliğiniz
            güncellenmez ve bitiş tarihinde hesabınız otomatik olarak kapanır.
          </Typography>
        </DialogContent>
        <DialogActions>
          <WhiteButton onClick={() => setConfirmRemoveCard(null)}>
            İptal
          </WhiteButton>
          <RedButton onClick={handleRemoveCard}>Sil</RedButton>
        </DialogActions>
      </Dialog>

      <Footer />

      {/* Bildirimler */}
      <Snackbar
        open={!!successMsg}
        autoHideDuration={3500}
        onClose={() => setSuccessMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessMsg("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMsg}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={3500}
        onClose={() => setErrorMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorMsg("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default ProfileSettings;
